import type { SummonKind } from "../../../data/portal-ffa-catalog";
import { clampSimDt } from "../../../sim";
import type { DropMode } from "../economy-rules";
import { applyTroopSummon } from "../economy-rules";
import { botTick } from "./bot-ai";
import { tickCollect, tickNaturalMana } from "./mana-orbs";
import { tickMovementCombat } from "./motion";
import { drainSummonQueues } from "./summon";
import type { BotPersonality, TeamState, WorldState } from "./types";
import {
  armyCount,
  C,
  ranking,
  setMessage,
  spawnMaster,
  spawnPoint,
  spawnTroop,
  unitDef,
} from "./shared";
import { tickEvents } from "./world-events";

function tickReenterBots(world: WorldState): void {
  for (const team of world.teams) {
    if (team.alive || team.isPlayer) continue;
    if (team.deadAt === null) continue;
    if (world.time - team.deadAt < C.session.reenterDelaySec + 2) continue;
    reenterTeam(world, team.id);
  }
}

export function reenterTeam(world: WorldState, teamId: number): void {
  const team = world.teams[teamId];
  if (!team || team.alive) return;
  const idx = teamId;
  const pos = spawnPoint(
    world.mapW,
    world.mapH,
    C.map.outerMargin,
    idx,
    world.teams.length,
  );
  const master = spawnMaster(world, teamId, pos.x, pos.y);
  team.masterId = master.id;
  team.alive = true;
  team.goalX = pos.x;
  team.goalY = pos.y;
  team.ledger = {
    walletMana: C.master.starterMana,
    aliveUnitValue: 0,
    specialResidualValue: 0,
    lifetimeEarnedMana: 0,
  };
  team.sigils = C.master.starterSigils;
  team.protectUntil = world.time + C.master.spawnProtectSec;
  team.summonCd = 0;
  team.summonQueue.length = 0;
  team.runStart = world.time;
  team.mastersKilled = 0;
  team.peakArmy = 0;
  team.peakRank = 99;
  team.deadAt = null;
  team.lastDeathDrop = 0;

  const starter = C.session.starterArmy;
  for (const [kind, n] of Object.entries(starter) as [SummonKind, number][]) {
    for (let i = 0; i < n; i++) {
      const cost = unitDef(kind).summonCost;
      if (team.ledger.walletMana < cost) break;
      team.ledger = applyTroopSummon(team.ledger, cost)!;
      spawnTroop(world, teamId, kind, pos.x, pos.y);
    }
  }
  if (team.isPlayer) {
    world.playerPendingReenter = false;
    world.lastRunSummary = null;
    setMessage(world, "hud.msgReenter");
  }
}

export function createWorld(opts?: { dropMode?: DropMode }): WorldState {
  const mapW = C.map.width;
  const mapH = C.map.height;
  const world: WorldState = {
    time: 0,
    nextId: 1,
    mapW,
    mapH,
    entities: [],
    teams: [],
    orbs: [],
    dropMode: opts?.dropMode ?? C.economy.dropMode,
    naturalManaCd: 1,
    nextPactAt: C.events.portalPactIntervalSec,
    nextWarpAt: C.events.warpCoreIntervalSec,
    alliance: null,
    warpCore: null,
    message: null,
    playerPendingReenter: false,
    lastRunSummary: null,
    eventsOut: [],
  };

  const personalities: BotPersonality[] = [
    "Collector",
    "Aggressive",
    "Opportunist",
    "Collector",
    "Aggressive",
  ];
  const total = 1 + C.session.botCount;

  for (let i = 0; i < total; i++) {
    const pos = spawnPoint(mapW, mapH, C.map.outerMargin, i, total);
    const team: TeamState = {
      id: i,
      nameKey: i === 0 ? "team.you" : "team.bot",
      nameParams: i === 0 ? undefined : { n: i },
      isPlayer: i === 0,
      alive: true,
      masterId: -1,
      goalX: pos.x,
      goalY: pos.y,
      ledger: {
        walletMana: C.master.starterMana,
        aliveUnitValue: 0,
        specialResidualValue: 0,
        lifetimeEarnedMana: 0,
      },
      sigils: C.master.starterSigils,
      protectUntil: C.master.spawnProtectSec,
      moveGraceUntil: 0,
      moveOrderActive: false,
      personality:
        i === 0 ? null : personalities[(i - 1) % personalities.length],
      summonCd: 0,
      summonQueue: [],
      runStart: 0,
      peakRank: 99,
      mastersKilled: 0,
      peakArmy: 0,
      deadAt: null,
      lastDeathDrop: 0,
      allianceGraceUntil: 0,
    };
    world.teams.push(team);
    const master = spawnMaster(world, i, pos.x, pos.y);
    team.masterId = master.id;

    const starter = C.session.starterArmy;
    for (const [kind, n] of Object.entries(starter) as [SummonKind, number][]) {
      for (let j = 0; j < n; j++) {
        const cost = unitDef(kind).summonCost;
        if (team.ledger.walletMana < cost) break;
        team.ledger = applyTroopSummon(team.ledger, cost)!;
        spawnTroop(world, i, kind, pos.x, pos.y);
      }
    }
    team.peakArmy = armyCount(world, i);
  }

  setMessage(world, "hud.msgStart");

  for (let c = 0; c < 6; c++) {
    world.naturalManaCd = 0;
    tickNaturalMana(world, 0);
  }
  world.naturalManaCd = C.economy.naturalManaIntervalSec;

  return world;
}

export function tickWorld(world: WorldState, dt: number): void {
  const step = clampSimDt(dt);
  world.time += step;

  for (const team of world.teams) {
    if (team.summonCd > 0) team.summonCd -= step;
  }
  drainSummonQueues(world);

  tickNaturalMana(world, step);
  tickCollect(world, step);
  tickMovementCombat(world, step);
  tickEvents(world, step);

  for (const team of world.teams) {
    if (team.alive && team.personality) botTick(world, team);
  }

  tickReenterBots(world);

  const ranks = ranking(world);
  ranks.forEach((t, i) => {
    t.peakRank = Math.min(t.peakRank, i + 1);
  });
}
